import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errText } from "@/lib/api";

const LABELS = {
  direct: "Direct / site",
  "lp:property-in-pune": "Ad — Pune",
  "lp:property-in-delhi-ncr": "Ad — Delhi NCR",
  "lp:property-in-mumbai": "Ad — Mumbai",
  "lp:list-your-property": "Ad — List your property",
  "lp:pg-and-coliving": "Ad — PG & co-living",
};

export const CampaignsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/campaigns");
      setRows(data);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const grand = rows.reduce((s, r) => s + r.total, 0);

  return (
    <div data-testid="admin-campaigns">
      <p className="mb-4 flex items-center gap-2 text-[13px] text-muted-foreground">
        <Megaphone className="h-4 w-4 text-primary" /> Where your enquiries come from — ad landing pages are
        tagged automatically, everything else counts as direct.
      </p>

      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        {[
          { label: "Total leads", value: grand },
          { label: "From campaigns", value: rows.filter((r) => r.source !== "direct").reduce((s, r) => s + r.total, 0) },
          { label: "Closed", value: rows.reduce((s, r) => s + r.closed, 0) },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl border border-border p-5">
            <p className="text-[12px] text-muted-foreground">{s.label}</p>
            <p className="mt-1 font-display text-2xl">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-border overflow-x-auto">
        <Table className="min-w-[760px]" data-testid="admin-campaigns-table">
          <TableHeader>
            <TableRow>
              <TableHead>Campaign</TableHead>
              <TableHead>Leads</TableHead>
              <TableHead>Share</TableHead>
              <TableHead>Contacted</TableHead>
              <TableHead>Visited</TableHead>
              <TableHead>Closed</TableHead>
              <TableHead>Last lead</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-sm text-muted-foreground">Loading…</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-sm text-muted-foreground">No enquiries yet.</TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.source} data-testid={`campaign-row-${r.source}`}>
                  <TableCell className="text-sm font-semibold">{LABELS[r.source] || r.source}</TableCell>
                  <TableCell className="text-sm">{r.total}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {grand ? `${Math.round((r.total / grand) * 100)}%` : "—"}
                  </TableCell>
                  <TableCell className="text-sm">{r.contacted}</TableCell>
                  <TableCell className="text-sm">{r.visited}</TableCell>
                  <TableCell className="text-sm">{r.closed}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">
                    {(r.last_at || "").slice(0, 16).replace("T", " ") || "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default CampaignsTab;
